import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Box, Button, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { getOrganizationsEnterpriseSelector } from "../../../../reducers/Organizations/organizations"
import { COLORS } from "../../../../utils"
import ContactCard from "./ContactCard"
import ContactFormModal from "./ContactFormModal"
import DeleteContactConfirmModal from "./DeleteContactConfirmModal"

const ContactsSection = () => {
  const enterprise = useSelector(getOrganizationsEnterpriseSelector)
  const [addOpen, setAddOpen] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [deletingContact, setDeletingContact] = useState(null)

  if (!enterprise) return null

  const contacts = enterprise.contacts || []
  const isEmpty = contacts.length === 0

  return (
    <Box sx={sx.section}>
      <Box className="flexRow center spaceBetween" sx={sx.header}>
        <Typography sx={sx.title}>Contacts</Typography>
        {!isEmpty && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} sx={sx.addBtn} onClick={() => setAddOpen(true)}>
            Ajouter un contact
          </Button>
        )}
      </Box>

      {isEmpty ? (
        <Box sx={sx.emptyBody}>
          <Box
            className="flexCenter pointer"
            sx={sx.dashedBtn}
            onClick={() => setAddOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setAddOpen(true)}
          >
            <Box className="flexClassicCenter" sx={sx.dashedIcon}>
              <AddIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={sx.dashedTitle}>Ajouter un contact</Typography>
            <Typography sx={sx.dashedSub}>Renseignez les personnes à contacter dans cette entreprise.</Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={sx.listBody}>
          {contacts.map((contact) => (
            <ContactCard key={contact.id} contact={contact} onEdit={setEditingContact} onDelete={setDeletingContact} />
          ))}
        </Box>
      )}

      <ContactFormModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        enterpriseId={enterprise.objectId}
        existingEmails={contacts.map(c => c.email)}
      />

      {editingContact && (
        <ContactFormModal
          open={Boolean(editingContact)}
          onClose={() => setEditingContact(null)}
          enterpriseId={enterprise.objectId}
          contact={editingContact}
          existingEmails={contacts.filter(c => c.id !== editingContact.id).map(c => c.email)}
        />
      )}

      {deletingContact && (
        <DeleteContactConfirmModal
          open={Boolean(deletingContact)}
          onClose={() => setDeletingContact(null)}
          enterpriseId={enterprise.objectId}
          contact={deletingContact}
        />
      )}
    </Box>
  )
}

const sx = {
  section: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    bgcolor: "background.paper",
    overflow: "hidden",
  },
  header: {
    px: 3,
    py: 2,
  },
  title: {
    fontWeight: 500,
    fontSize: 18,
    color: "text.primary",
  },
  addBtn: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  emptyBody: {
    px: 3,
    pb: 3,
  },
  dashedBtn: {
    border: "1.5px dashed",
    borderColor: COLORS.BLACK_300,
    borderRadius: "8px",
    py: "32px",
    px: "20px",
    textAlign: "center",
    gap: "8px",
    bgcolor: COLORS.GREY_SUPPLIER_BACKGROUND,
    transition: "all 150ms",
    "&:hover": {
      borderColor: "primary.main",
      bgcolor: "#E8F1FE",
      "& .dashed-icon": {
        borderColor: "primary.main",
        color: "primary.main",
      },
    },
  },
  dashedIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    bgcolor: "white",
    border: "1.5px solid",
    borderColor: COLORS.BLACK_300,
    color: COLORS.DRAFT_GREY,
    transition: "all 150ms",
  },
  dashedTitle: {
    fontSize: 15,
    fontWeight: 500,
    color: COLORS.BLACK800,
    mt: "4px",
  },
  dashedSub: {
    fontSize: 13,
    color: COLORS.DRAFT_GREY,
    maxWidth: 380,
    lineHeight: 1.45,
  },
  listBody: {
    px: 3,
    pb: 3,
    marginTop: "16px",
  },
}

export default ContactsSection
